/**
 * BatchPanel — AI 복사 배치 화면 (계약 BE-20260825-exam-papers-screens.md §4·§5-3).
 *
 * AiCopyModal 이 시작한 배치 하나를 끝까지 끌고 간다: 진행률 훑기 → 문항별 검수(이전/다음) →
 * 조립 → 새 시험지. 멤버는 단건 변형과 완전히 같은 실체(GET /variations/{vid})라 검수 카드를
 * 그대로 재사용한다 — 여기서 새로 하는 일은 "여러 건을 한 배치로 몰아 보는 것"뿐이다.
 *
 * 이 화면의 중심은 조립 게이트다(§4): 승인도 폐기도 아닌 문항이 하나라도 남으면 조립은 409다.
 * 그래서 "일부만 조립하려면 나머지를 폐기하면 된다"를 상시로 적어 둔다 — 이걸 모르면 쓸모없는
 * 문항 하나 때문에 배치 전체가 막힌 것처럼 보인다.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ArrowRight, AlertTriangle, Check, ChevronLeft, ChevronRight, RefreshCw, Package, SkipForward, Trash2 } from 'lucide-react';
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import VariationReviewCard from '../ProblemHubV2/VariationReviewCard';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다)
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
import {
  getJobStatusDisplay, getReviewStatusDisplay, retryVariation, rejectVariation, approveVariation,
  POLLING_STATUSES, JOB_POLL_MS,
} from '../ProblemHubV2/variationApi';
import { BANK_APPROVE_ROLES } from '../../utils/roles';
import {
  fetchBatch, assembleBatch, discardBatch, listBatches, isSettled, describeApiError,
  ALL_REJECTED_TEXT, BATCH_STATUS_DISCARDED, AUTH_REDIRECT,
} from './examPaperApi';

/** 멤버 배열 = items (§2-6). 형태는 확정됐어도 값은 비어 올 수 있다 */
function toMembers(d) {
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.items) ? d.items : [];
}

const memberVid = (m) => m?.variation_id ?? null;
const sourceProblemId = (m) => m?.source_problem_id ?? null;

/**
 * 시험지 표기 — **이름이 정본이고 번호는 뒤에 붙인다**(계약 BE-20260826 §4 D8: 배치 상세에
 * `source_paper_title`·`result_paper_title` 이 추가됐다). 번호만 있던 동안에는 배치를 열어도
 * 무엇을 복사한 것인지 알 수 없어, 시험지 목록으로 돌아가 대조해야 했다.
 * 이름이 안 실린 응답(목록으로 되짚은 결과 시험지 등)에서는 번호만 남긴다 — 빈칸으로 두지 않는다.
 */
function paperLabel(title, id) {
  const name = typeof title === 'string' ? title.trim() : '';
  const hasId = id != null && id !== '';
  if (name) return hasId ? `${name} (#${id})` : name;
  return hasId ? `#${id}` : null;
}

/**
 * order_no 순이 계약이지만 그 순서가 곧 새 시험지의 출제 순서라, 서버가 어긋나게 줘도
 * 화면에서는 맞춰 보여 준다. order_no 가 아예 없으면 정렬이 안정적이라 응답 순서가 남는다.
 */
function orderMembers(list) {
  return [...list].sort((a, b) => (a?.order_no ?? 0) - (b?.order_no ?? 0));
}

/**
 * 집계 — counts 키가 확정됐다(§2-5: total/pending/processing/done/failed/approved/rejected).
 * 서버 값을 먼저 쓰되, 비어 온 자리는 멤버에서 센다(멤버는 항상 전량이라 같은 수가 나온다).
 * 미검수만은 멤버에서 센다 — counts 에 키가 없고, 이 수가 곧 화면이 옮겨 다닐 대상이다.
 */
function tally(batch, members) {
  const local = { wait: 0, run: 0, done: 0, failed: 0, approved: 0, rejected: 0, total: members.length, unsettled: 0 };
  members.forEach((m) => {
    // 110(큐 대기)도 대기로 센다 — 빠뜨리면 그 문항이 어느 배지에도 안 잡혀 합이 어긋난다
    if (m?.status === 100 || m?.status === 110) local.wait += 1;
    else if (m?.status === 150) local.run += 1;
    else if (m?.status === 500) local.failed += 1;
    else if (m?.status === 200) local.done += 1;
    if (m?.review_status === 200) local.approved += 1;
    else if (m?.review_status === 400) local.rejected += 1;
    if (!isSettled(m)) local.unsettled += 1;
  });
  const c = batch?.counts;
  if (!c || typeof c !== 'object') return local;
  const pick = (v, fallback) => (typeof v === 'number' ? v : fallback);
  return {
    wait: pick(c.pending, local.wait),
    run: pick(c.processing, local.run),
    done: pick(c.done, local.done),
    failed: pick(c.failed, local.failed),
    approved: pick(c.approved, local.approved),
    rejected: pick(c.rejected, local.rejected),
    total: pick(c.total, local.total),
    unsettled: local.unsettled,
  };
}

const COUNT_BADGES = [
  { key: 'wait', label: '대기', cls: getJobStatusDisplay(100).cls },
  { key: 'run', label: '생성 중', cls: getJobStatusDisplay(150).cls },
  { key: 'done', label: '생성 완료', cls: getJobStatusDisplay(200).cls },
  { key: 'failed', label: '실패', cls: getJobStatusDisplay(500).cls },
  { key: 'approved', label: '승인', cls: getReviewStatusDisplay(200).cls },
  { key: 'rejected', label: '폐기', cls: getReviewStatusDisplay(400).cls },
];

function BatchPanel({ batchId, onBack = null, onAssembled = null, onDiscarded = null, onDiscardFailed = null, onDirtyChange = null }) {
  const { getAccessToken, userType } = useAuth();
  // 배치 폐기는 서버가 매니저에게만 연다(문항 반려와 같은 게이트). 권한이 없으면 버튼을 그리지 않는다
  const canDiscard = BANK_APPROVE_ROLES.includes(userType);
  const [batch, setBatch] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedVid, setSelectedVid] = useState(null);
  const [assembling, setAssembling] = useState(false);
  const [confirmAssemble, setConfirmAssemble] = useState(false);
  // 확인 바가 "미저장 편집이 사라진다"까지 말해야 하므로 깃발을 state 로도 든다(ref 만으로는 다시 안 그린다)
  const [cardDirty, setCardDirty] = useState(false);
  // 조립 결과 — 이 세션의 조립 응답, 또는 상세가 안 줄 때 목록에서 되짚은 값.
  // '' 는 "조립했지만 시험지 번호를 못 받았다" — null(미조립)과 구분한다
  const [knownPaperId, setKnownPaperId] = useState(null);
  const [retryingVid, setRetryingVid] = useState(null);
  const [rejectingVid, setRejectingVid] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  // 전체 승인 진행 — { cur, total }. null 이면 돌고 있지 않다
  const [approvingAll, setApprovingAll] = useState(null);
  // 검수 카드를 새로 읽힐 때 올린다 — 카드 밖에서 승인하면 카드는 그 사실을 모른다
  const [cardNonce, setCardNonce] = useState(0);

  // 열린 검수 카드의 미저장 여부 — 문항을 넘기면 카드가 언마운트되며 편집이 사라진다
  const dirtyRef = useRef(false);
  // 페이지도 같은 사실을 알아야 한다(주소를 옮기기 전에 물어야 하므로)
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  const seqRef = useRef(0);
  // 로딩 표시는 화면을 여는 조회의 것이다 — 무효화 카운터를 같이 쓰면 6초 폴링이 번호를 올리는
  // 순간 새로고침 버튼이 영영 돌면서 잠긴다.
  const uiSeqRef = useRef(0);
  const probedRef = useRef(null); // 조립 결과 되짚기를 배치당 한 번으로 묶는다
  const mountedRef = useRef(true);
  const reviewRef = useRef(null);
  // 검수 카드가 담기는 칸 — 문항을 넘길 때 스크롤을 맨 위로 되돌린다
  const cardScrollRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true; // StrictMode 재마운트에서 false 로 굳지 않게 마운트마다 세운다
    return () => { mountedRef.current = false; };
  }, []);
  // 패널이 사라지면 미저장도 함께 사라진다 — 깃발을 남기면 페이지가 영원히 이동을 막는다
  useEffect(() => () => onDirtyRef.current?.(false), []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (batchId == null) return;
    const seq = (seqRef.current += 1); // 늦게 도착한 이전 응답이 최신 상태를 덮지 않게
    if (!silent) { uiSeqRef.current = seq; setLoading(true); setError(null); }
    try {
      const d = await fetchBatch(batchId, getAccessToken);
      if (!mountedRef.current || seq !== seqRef.current) return;
      setBatch(d);
      setMembers(orderMembers(toMembers(d)));
    } catch (e) {
      if (!mountedRef.current || seq !== seqRef.current) return;
      if (silent) {
        // 목록을 지우지 않는다 — 이 목록이 곧 폴링의 근거라, 비우면 폴링도 함께 멎는다
        setToast({ type: 'error', text: '배치 상태를 갱신하지 못했습니다 — 새로고침해 주세요.' });
        return;
      }
      setError(e.message || '배치를 불러오지 못했습니다.');
      setBatch(null);
      setMembers([]);
    } finally {
      if (mountedRef.current && !silent && uiSeqRef.current === seq) setLoading(false);
    }
  }, [batchId, getAccessToken]);

  useEffect(() => {
    setBatch(null);
    setMembers([]);
    setSelectedVid(null);
    setToast(null);
    setKnownPaperId(null);
    setConfirmAssemble(false);
    dirtyRef.current = false;
    setCardDirty(false);
    onDirtyRef.current?.(false);
    load();
  }, [load]);

  // 타이머는 여기 하나뿐이다. 진행 중인 멤버가 없어지거나(anyRunning=false), batchId 가 바뀌거나
  // (load 교체), 패널이 사라지면 같은 cleanup 이 걷어 간다. load 는 매 tick 서버를 다시 읽으므로
  // 낡은 멤버 목록을 붙든 클로저가 생기지 않는다.
  const anyRunning = members.some((m) => POLLING_STATUSES.includes(m?.status));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => load({ silent: true }), JOB_POLL_MS);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const counts = tally(batch, members);
  const total = counts.total || members.length;
  const finished = counts.done + counts.failed; // 실패도 '더 기다릴 것이 없다'는 뜻이라 막대에는 함께 찬다
  const pct = total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0;
  const allSettled = members.length > 0 && members.every(isSettled);
  // 이미 조립됐는지는 계약이 result_paper_id 로 답한다(§1-2) — 이 세션의 조립 결과가 우선이다
  const assembledPaperId = knownPaperId ?? batch?.result_paper_id ?? null;
  const assembled = assembledPaperId != null;
  // 폐기한 배치 — 상세의 status 로 안다(BE-20261102 회신 §1). 주소로 다시 열면 이 상태로 뜬다
  const discarded = batch?.status === BATCH_STATUS_DISCARDED;
  // 무엇을 무엇으로 복사한 배치인가(계약 §4 D8). 결과 쪽은 조립 전이면 아직 없다 —
  // '' 는 "조립했지만 번호를 못 받았다"라 번호 자리를 비운다.
  const sourcePaperLabel = paperLabel(batch?.source_paper_title, batch?.source_paper_id);
  const resultPaperLabel = paperLabel(batch?.result_paper_title, assembledPaperId === '' ? null : assembledPaperId);

  // 상세에 result_paper_id 가 실리는지는 계약이 목록(§1-2)에서만 말한다 — 없으면 목록으로 되짚는다.
  // 조립 결과를 브라우저에 적어 두던 우회를 대신하는 자리다(뒤로 갔다 돌아와도 '이미 조립됨'이 남게).
  // 폴링이 batch 를 매번 새 객체로 갈아 끼우므로, 되짚기는 배치당 한 번만 돈다.
  useEffect(() => {
    if (batch == null || knownPaperId != null) return undefined;
    if (batch.result_paper_id !== undefined) return undefined; // 상세가 이미 답했다(null 이면 미조립)
    const sourcePaperId = batch.source_paper_id;
    if (sourcePaperId == null || probedRef.current === String(batchId)) return undefined;
    probedRef.current = String(batchId);
    let alive = true;
    (async () => {
      try {
        const { items } = await listBatches({ sourcePaperId, perPage: 50 }, getAccessToken);
        if (!alive || !mountedRef.current) return;
        const row = items.find((b) => String(b?.batch_id) === String(batchId));
        if (row?.result_paper_id != null) setKnownPaperId(row.result_paper_id);
      } catch (_) { /* 보조 경로다 — 실패해도 조립 버튼은 그대로 쓸 수 있다 */ }
    })();
    return () => { alive = false; };
  }, [batch, knownPaperId, batchId, getAccessToken]);

  // 확인 바가 떠 있는 동안 승인·폐기 수가 바뀌면(폴링·다른 검수자) 사용자가 보고 승낙한 숫자와
  // 달라진다 — 그 승낙은 무효다. 다시 열어 새 숫자를 보게 한다.
  useEffect(() => { setConfirmAssemble(false); }, [counts.approved, counts.rejected, counts.total]);

  // 미저장 편집을 조용히 버리지 않는다 — 이동·뒤로가 여길 지난다(조립은 인라인 확인 바가 같은 일을
  // 하되 숫자와 함께 화면에 남긴다). 묻기만 한다: 깃발은 카드가 실제로 사라지는 길에서만 내린다.
  const askDiscard = () => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('저장하지 않은 검수 수정이 있습니다. 버리고 계속할까요?');
  };

  const clearDirty = () => {
    dirtyRef.current = false;
    setCardDirty(false);
    onDirtyChange?.(false);
  };

  const selectMember = (m) => {
    const vid = memberVid(m);
    if (vid == null || String(vid) === String(selectedVid)) return;
    if (!askDiscard()) return;
    clearDirty(); // key 가 바뀌며 카드가 새로 마운트된다 — 편집은 여기서 사라진다
    setSelectedVid(vid);
  };

  // 좁은 화면에서는 목록이 카드 위에 쌓인다(flex-col) — 고른 문항이 화면 밖에서 바뀌면
  // 눌렀는데 아무 일도 없는 것처럼 보인다. block:'nearest' 라 이미 보이는 데스크톱은 그대로다.
  useEffect(() => {
    if (selectedVid == null) return;
    reviewRef.current?.scrollIntoView({ block: 'nearest' });
    // 카드 칸이 스스로 스크롤하므로, 앞 문항에서 내려 둔 위치가 그대로 남는다 — 다음 문항이
    // 중간부터 보이면 지금 무엇을 보고 있는지 알 수 없다
    if (cardScrollRef.current) cardScrollRef.current.scrollTop = 0;
  }, [selectedVid]);

  const curIdx = members.findIndex((m) => String(memberVid(m)) === String(selectedVid));
  const prevMember = curIdx > 0 ? members[curIdx - 1] : null;
  // 아직 아무것도 안 열었으면 [다음]은 첫 문항을 연다 — 검수를 시작하는 가장 짧은 길
  const nextMember = curIdx < 0 ? (members[0] ?? null) : (members[curIdx + 1] ?? null);

  const goNextUnsettled = () => {
    const start = curIdx >= 0 ? curIdx + 1 : 0;
    // 뒤로 훑다 끝에 닿으면 앞으로 되감는다 — 건너뛴 앞쪽 미검수가 남아 조립이 막히는 일이 없게
    const ring = [...members.slice(start), ...members.slice(0, start)];
    const target = ring.find((m) => !isSettled(m) && memberVid(m) != null);
    if (!target) {
      setToast({ type: 'info', text: '미검수 문항이 없습니다 — 이제 조립할 수 있습니다.' });
      return;
    }
    selectMember(target);
  };

  const goPrev = () => selectMember(prevMember);
  const goNext = () => selectMember(nextMember);

  // ← → 로 이전/다음. 두 함수는 매 렌더 새로 만들어지므로 리스너에 직접 넣으면 떼었다 붙이기를
  // 반복한다 → 리스너는 한 번만 걸고 최신 함수만 ref 로 갈아 끼운다(OcrReviewModal 과 같은 방식).
  const navRef = useRef({ goPrev, goNext });
  useEffect(() => { navRef.current = { goPrev, goNext }; });
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = document.activeElement;
      // 글을 고치는 중이면 커서 이동이 우선이다(검수 카드는 입력칸투성이다)
      if (el?.isContentEditable) return;
      if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.tagName === 'SELECT') return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') navRef.current.goPrev();
      else navRef.current.goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * 나가기 — **끝나지 않은 배치라도 묻지 않는다**(오너 지시 2026-08-26).
   *
   * 예전에는 "미검수 N개가 남았다 · 나가도 생성은 계속된다 · 주소로 다시 열 수 있다"를
   * 확인 창으로 말했다. 그 사실들이 전부 참이라 **잃는 것이 없고**, 되돌아갈 길은 목록 위
   * 「진행중인 AI변형 시험지」 줄이 상시로 보여 준다 — 잃을 것이 없는 이동을 막는 창은
   * 그냥 한 번 더 누르는 일이다.
   *
   * 미저장 검수 수정만은 그대로 묻는다(askDiscard). 그것은 나가면 사라지고 다시 볼 수 없다.
   */
  const handleBack = () => {
    if (!askDiscard()) return;
    clearDirty();
    onBack && onBack();
  };

  const retryMember = async (m) => {
    const vid = memberVid(m);
    if (vid == null || retryingVid != null) return;
    setRetryingVid(vid);
    setToast(null);
    try {
      const d = await retryVariation(vid, getAccessToken);
      if (!mountedRef.current || d === AUTH_REDIRECT) return;
      const which = m?.order_no != null ? `${m.order_no}번 문항` : '해당 문항';
      // 데몬이 직렬이라 "수십 초"는 앞에 아무것도 없을 때의 값이다(계약 §4 「데몬」)
      setToast({ type: 'info', text: `${which}을 다시 생성 요청했습니다 — 대기열이 비어 있으면 수십 초 걸립니다.` });
      await load({ silent: true });
    } catch (e) {
      if (!mountedRef.current) return;
      setToast({ type: 'error', text: e.message || '재시도에 실패했습니다.' });
      // 409 는 화면이 낡았다는 뜻이다 — 다른 사람이 먼저 폐기했으면 버튼이 사라져야 한다(회신 §2)
      if (e?.httpStatus === 409) load({ silent: true });
    } finally {
      if (mountedRef.current) setRetryingVid(null);
    }
  };

  /**
   * 문항 폐기 — 검수 카드는 생성이 끝난 뒤(200·500)에만 폐기를 연다. 그런데 조립을 막는 것은
   * '정리되지 않은 문항'이지 '완료된 문항'이 아니다. 대기·생성 중에서 멈춘 한 건 때문에 배치
   * 전체가 잠기는 일이 없도록, 목록 행에서 상태와 무관하게 폐기할 수 있게 둔다(계약 §2 —
   * 폐기에 상태 제한이 없다).
   */
  const rejectMember = async (m) => {
    const vid = memberVid(m);
    if (vid == null || rejectingVid != null) return;
    const which = m?.order_no != null ? `${m.order_no}번 문항` : '이 문항';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${which}을 폐기할까요?\n새 시험지에서 빠지고, 조립 게이트에서는 정리된 것으로 셉니다.`)) return;
    setRejectingVid(vid);
    setToast(null);
    try {
      const d = await rejectVariation(vid, getAccessToken);
      if (!mountedRef.current || d === AUTH_REDIRECT) return;
      setToast({ type: 'info', text: `${which}을 폐기했습니다 — 새 시험지에서 빠집니다.` });
      await load({ silent: true });
    } catch (e) {
      if (mountedRef.current) setToast({ type: 'error', text: e.message || '폐기하지 못했습니다.' });
    } finally {
      if (mountedRef.current) setRejectingVid(null);
    }
  };

  // 전체 승인의 대상 — 생성이 끝났고(200) 아직 승인도 폐기도 안 한 문항. 대기·생성 중·실패는 서버가
  // 승인을 받지 않는다(검수 카드도 생성이 끝난 뒤에만 [승인]을 연다).
  const approvable = members.filter((m) => memberVid(m) != null && m?.status === 200 && !isSettled(m));

  /**
   * 전체 승인 — [시험지 생성]을 여는 길이 「미검수 0」뿐이라, 남은 승인을 한 번에 끝낸다(오너 지시 2026-10-09).
   * 한 번에 승인하는 API 가 없어 한 문항씩 차례로 부른다. 한 건이 막혀도(그림 자리표시자 409 등)
   * 나머지는 계속하고, 무엇이 왜 남았는지는 끝에 번호로 말한다.
   * 열린 카드의 미저장 수정은 문항을 옮길 때와 같은 말로 묻는다(오너 결정 같은 날) — 승인은 서버 값으로
   * 나가므로, 저장 안 한 수정은 담기지 않는다.
   */
  const approveAll = async () => {
    if (approvingAll || assembling || approvable.length === 0) return;
    const targets = approvable;
    const skipped = counts.unsettled - targets.length;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `생성이 끝난 ${targets.length}문항을 문제은행에 새 문제로 등록합니다.\n`
      + '원본 문제는 그대로 두고 새 문제가 생깁니다.\n'
      + (skipped > 0 ? `대기·생성 중·실패 ${skipped}문항은 빠집니다.\n` : '')
      + '\n모두 승인할까요?',
    )) return;
    if (!askDiscard()) return;
    if (dirtyRef.current) {
      // 버리기로 했다 — 카드를 새로 읽혀 화면에 남은 편집도 지운다
      clearDirty();
      setCardNonce((n) => n + 1);
    }
    setToast(null);
    let ok = 0;
    const failed = [];
    try {
      for (let i = 0; i < targets.length; i += 1) {
        const m = targets[i];
        setApprovingAll({ cur: i + 1, total: targets.length });
        try {
          const d = await approveVariation(memberVid(m), getAccessToken);
          // 401 이면 쓰기 헬퍼가 /signin 으로 보낸다 — 남은 건을 더 부르지 않는다
          if (d === AUTH_REDIRECT || !mountedRef.current) return;
          ok += 1;
        } catch (e) {
          if (!mountedRef.current) return;
          failed.push({
            order: m?.order_no ?? members.indexOf(m) + 1,
            reason: e?.data?.reason === 'image_placeholder'
              ? '본문에 [그림] 자리표시자가 남아 있음'
              : (e?.message || '승인 실패'),
          });
        }
      }
    } finally {
      if (mountedRef.current) setApprovingAll(null);
    }
    setCardNonce((n) => n + 1); // 열린 카드가 「이미 승인된 결과」로 보이게
    if (failed.length === 0) {
      setToast({ type: 'info', text: `${ok}문항을 승인했습니다 — 문제은행에 새 문제로 등록됐습니다.` });
    } else {
      const list = failed.slice(0, 3).map((f) => `${f.order}번(${f.reason})`).join(', ')
        + (failed.length > 3 ? ` 외 ${failed.length - 3}문항` : '');
      setToast({
        type: 'error',
        text: `${ok}문항 승인 · ${failed.length}문항 실패 — ${list}. 실패한 문항은 검수 카드에서 고친 뒤 승인하세요.`,
      });
    }
    await load({ silent: true });
  };

  // 확인 바의 [생성 확정]만 여기로 온다 — 되돌릴 수 없는 조작이라 클릭 한 번으로는 나가지 않는다.
  // 조립은 카드를 언마운트하지 않는다 — 409 로 되돌아오면 편집이 그대로 남아 있으므로,
  // 성공해서 이 화면을 떠날 때에만 깃발을 내린다.
  const assemble = async () => {
    if (assembling || discarding || discarded || !allSettled) return;
    setAssembling(true);
    setToast(null);
    try {
      const d = await assembleBatch(batchId, getAccessToken);
      // 401 이면 쓰기 헬퍼가 /signin 으로 보내며 표식을 돌려준다 — 만들었다고 말하면 거짓말이 된다
      if (d === AUTH_REDIRECT || !mountedRef.current) return;
      const paperId = d?.paper_id ?? null; // 응답 id 확정(§2-8)
      setKnownPaperId(paperId ?? '');
      setConfirmAssemble(false);
      clearDirty();
      setToast({ type: 'info', text: paperId != null ? `새 시험지 #${paperId} 를 만들었습니다.` : '새 시험지를 만들었습니다.' });
      onAssembled && onAssembled(paperId);
    } catch (e) {
      if (!mountedRef.current) return;
      // 409 는 봉투의 data.reason 으로 갈래가 온다(§2-10) — 문구가 바뀌어도 판별은 흔들리지 않는다
      const reason = e?.data?.reason;
      if (reason === 'unsettled_members') {
        const ids = Array.isArray(e.data.variation_ids) ? e.data.variation_ids : [];
        const which = ids.length > 0
          ? `미검수 ${ids.length}문항(#${ids.slice(0, 5).join(', #')}${ids.length > 5 ? ' 외' : ''})이 남아`
          : '아직 정리되지 않은 문항이 있어';
        // 화면이 본 것과 서버가 아는 것이 어긋났다(다른 검수자가 되돌렸거나 재시도가 걸렸다)
        setToast({ type: 'error', text: `${which} 조립하지 못했습니다 — 승인하거나 폐기하면 열립니다. 최신 상태를 다시 읽습니다.` });
        load({ silent: true });
      } else if (reason === 'all_rejected') {
        setToast({ type: 'error', text: ALL_REJECTED_TEXT });
        load({ silent: true });
      } else if (reason === 'batch_discarded') {
        // 다른 창에서 먼저 폐기했다 — 다시 읽으면 폐기한 배치 화면으로 바뀐다
        setToast({ type: 'error', text: describeApiError(e) });
        load({ silent: true });
      } else if (reason === 'empty_batch') {
        setToast({ type: 'error', text: '이 배치에는 문항이 없습니다 — 원본 시험지에 문항이 없었던 것으로 보입니다.' });
      } else if (e?.httpStatus === 409) {
        setToast({ type: 'error', text: e.message || '지금 상태로는 조립할 수 없습니다 — 최신 상태를 다시 읽습니다.' });
        load({ silent: true });
      } else {
        setToast({ type: 'error', text: e.message || '조립하지 못했습니다.' });
      }
    } finally {
      if (mountedRef.current) setAssembling(false);
    }
  };

  /**
   * 배치 폐기 — 시험지를 만들지 않고 배치를 끝낸다(회신 BE-20261102 §1). 전부 폐기한 배치는 시험지 생성이
   * 거절되어(all_rejected) 「진행중」 줄에서 뺄 길이 이것뿐이다. 되돌릴 수 없어 무엇이 어떻게 되는지 숫자로 묻는다.
   * 미저장 수정은 문항을 옮길 때와 같은 말로 묻는다 — 폐기하면 그 수정을 담을 곳이 없다.
   */
  const runBatchDiscard = async () => {
    if (discarding || assembling || approvingAll != null || batch == null || assembled || discarded) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      '이 AI변형 시험지를 폐기할까요? 되돌릴 수 없습니다.\n\n'
      + '「진행중인 AI변형 시험지」에서 사라지고, 새 시험지는 만들어지지 않습니다. 원본 시험지는 그대로입니다.'
      + (counts.wait > 0 ? `\n대기 중인 ${counts.wait}문항은 AI에 요청하지 않고 취소합니다. 폐기 전에 생성이 시작된 문항은 취소되지 않습니다.` : '')
      // 서버는 이미 보낸 요청을 끊지 않는다(회신 §1) — 전부 멈춘다고 읽히지 않게, 비용이 나가는 사실까지 말한다
      + (counts.run > 0 ? `\n생성 중인 ${counts.run}문항은 이미 AI에 요청해 취소되지 않습니다. 끝까지 만들어지고 비용도 들지만, 결과는 쓸 수 없습니다.` : '')
      + (counts.approved > 0 ? `\n승인한 ${counts.approved}문항은 문제은행에 그대로 남습니다.` : ''),
    )) return;
    if (!askDiscard()) return;
    setDiscarding(true);
    setToast(null);
    try {
      const d = await discardBatch(batchId, getAccessToken);
      // 401 이면 쓰기 헬퍼가 /signin 으로 보내며 표식을 돌려준다 — 폐기했다고 말하면 거짓말이 된다
      if (d === AUTH_REDIRECT) return;
      if (mountedRef.current) {
        clearDirty();
        setCardNonce((n) => n + 1); // 열린 카드가 「폐기된 결과」로 보이게
        setToast({ type: 'info', text: '시험지를 폐기했습니다.' });
        await load({ silent: true }); // 번호를 올려, 폐기 전에 나간 폴링 응답이 이 결과를 덮지 못하게 한다
      }
      // 응답을 기다리는 사이 이 패널이 사라졌어도([뒤로]·목록 펼치기) 페이지는 결과를 알아야 한다.
      // 서버는 이미 폐기했다 — 알리지 않으면 「진행중」 줄에 그대로 남는다.
      onDiscarded?.(batchId);
    } catch (e) {
      const text = describeApiError(e, '시험지를 폐기하지 못했습니다.');
      if (!mountedRef.current) {
        onDiscardFailed?.(text); // 패널이 사라져 여기 띄울 자리가 없다 — 실패를 삼키지 않는다
        return;
      }
      setToast({ type: 'error', text });
      // already_assembled 는 화면이 낡았다는 뜻이다. busy 도 읽어서 손해 볼 것이 없다
      if (e?.httpStatus === 409) load({ silent: true });
    } finally {
      if (mountedRef.current) setDiscarding(false);
    }
  };

  if (batchId == null) return null;

  /**
   * 이 화면은 **자기 높이 안에서 끝난다** — 머리(진행률)는 늘 서 있고, 아래 두 칸(문항 목록·검수
   * 카드)이 각자 스크롤한다. 한 덩어리로 흘려 두면 검수 카드 한 장이 길어질 때 페이지 전체가
   * 늘어나, 다음 문항으로 넘기려면 매번 위로 되감아야 했다(오너 지적).
   */
  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* 머리 — 진행률·집계·뒤로. 줄지 않는다(여기가 줄면 진행률이 먼저 사라진다) */}
      <div className="shrink-0 rounded-xl border border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleBack}
            disabled={!onBack}
            className="flex items-center gap-0.5 rounded-full px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-30"
          >
            <ArrowLeft className="size-3.5" />
            뒤로
          </button>
          <span className="text-sm font-bold text-gray-800">AI 복사 배치 #{batchId}</span>
          {batch?.title && <span className="min-w-0 truncate text-xs text-gray-500">{batch.title}</span>}
          {batch?.level != null && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">강도 L{batch.level}</span>
          )}
          {discarded && (
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${getReviewStatusDisplay(400).cls}`}>폐기됨</span>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {/* 시험지 폐기(API 는 배치 폐기) — 화면에서는 「배치」를 쓰지 않는다(오너 지시 2026-11-02). 모양은 같은 줄의 [뒤로]와 같고 색만 지우는 일의 빨강이다. 문항 줄의 휴지통과 헷갈리지 않게
                글자를 함께 쓴다. 시험지를 만든 배치·이미 폐기한 배치에는 할 일이 없어 그리지 않는다. */}
            {canDiscard && batch != null && !assembled && !discarded && (
              <button
                type="button"
                onClick={runBatchDiscard}
                disabled={discarding || assembling || approvingAll != null}
                className="flex items-center gap-0.5 rounded-full px-2 py-1 text-xs text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                {discarding ? '폐기 중…' : '시험지 폐기'}
              </button>
            )}
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              aria-label="배치 상태 새로고침"
              title="새로고침"
              className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            >
              <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* 원본 시험지 → 결과 시험지. 조립이 끝난 배치면 결과 이름이 곧 그 시험지로 가는 길이다
            (아래 조립 칸의 [만든 시험지 보기]와 같은 곳으로 간다 — 이름을 눌러 갈 수 있어야
            "이게 무엇이 됐는지"를 읽은 자리에서 바로 확인한다). */}
        {(sourcePaperLabel || resultPaperLabel || assembled) && (
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1 text-[11px] text-gray-500">
            <span className="shrink-0">원본</span>
            <span className="min-w-0 max-w-full truncate font-medium text-gray-700">{sourcePaperLabel || '알 수 없음'}</span>
            <ArrowRight aria-hidden="true" className="size-3 shrink-0 text-gray-400" />
            <span className="shrink-0">결과</span>
            {!assembled && (
              <span className="shrink-0 text-gray-400">{discarded ? '없음 — 폐기했습니다' : '아직 없음 — 조립하면 만들어집니다'}</span>
            )}
            {/* 조립은 됐는데 이름도 번호도 없는 경우가 있다 — 조립 응답이 paper_id 를 안 줬을 때다
                ('' 로 남긴다). 그때 '아직 없음'이라 말하면 화면이 거짓말을 한다 */}
            {assembled && !resultPaperLabel && (
              <span className="shrink-0 text-gray-400">만들어졌지만 번호를 받지 못했습니다</span>
            )}
            {assembled && resultPaperLabel && (
              onAssembled && assembledPaperId !== '' ? (
                <button
                  type="button"
                  onClick={() => onAssembled(assembledPaperId)}
                  className="min-w-0 max-w-full truncate font-medium text-primary underline transition-colors hover:text-primary/70"
                >
                  {resultPaperLabel}
                </button>
              ) : (
                <span className="min-w-0 max-w-full truncate font-medium text-gray-700">{resultPaperLabel}</span>
              )
            )}
          </div>
        )}

        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-gray-600">
              생성 완료 {counts.done} / {total}
              {counts.failed > 0 && <span className="ml-1 text-red-500">· 실패 {counts.failed}</span>}
            </span>
            <span className="font-bold text-gray-900">{pct}%</span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-label="배치 생성 진행률"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`h-2 rounded-full transition-all duration-300 ${counts.failed > 0 ? 'bg-yellow-500' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1">
          {COUNT_BADGES.map((b) => (
            <span key={b.key} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${b.cls}`}>
              {b.label} {counts[b.key]}
            </span>
          ))}
        </div>

        {anyRunning && (
          <p aria-live="polite" aria-busy="true" className="mt-1.5 text-[11px] text-gray-500">
            {/* 데몬은 직렬이고 대량 OCR 이 먼저다(계약 §4 「데몬」 — BE-20260824 의 "병렬" 문구 정정).
                문항 수만큼 동시에 도는 것으로 읽히면, 밀린 대기를 고장으로 본다 */}
            AI가 문항을 <b>한 건씩 차례로</b> 만들고 있습니다 — 문항당 보통 수십 초이고 자동으로 갱신됩니다.
            대량 OCR 작업이 앞서 있으면 그만큼 밀립니다.
          </p>
        )}
      </div>

      {/* 라이브 리전은 항상 마운트해 두고 문구만 바꾼다 — 내용과 함께 삽입되면 낭독이 불안정하다 */}
      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`shrink-0 text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>

      {/* 조립 게이트 — 계약 §4가 "반드시 노출"로 못박은 안내. 상태와 무관하게 늘 보인다.
          화면 문구에서는 '조립'을 쓰지 않는다(오너 지시 2026-08-31) — 여기 오는 사람에게 이 일의
          이름은 '시험지 생성'이다. 계약·API·변수명(assemble)은 그대로 둔다. */}
      <div className="shrink-0 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Package aria-hidden="true" className="size-4 text-gray-500" />
          <span className="text-sm font-bold text-gray-800">새 시험지 만들기</span>
        </div>

        {/* 확인 바 — 무엇이 들어가고 무엇이 빠지는지 숫자로 보인 뒤 [생성 확정]을 따로 받는다
            (AssignPanel 의 배포 확인과 같은 결).
            **두 가지만 말한다**(오너 지시 2026-08-31 「다 지우고, 승인한 문항과 폐기한 문항 수 표시,
            새 시험지는 AI 변형 으로 표시된다고」). 걷어낸 것 셋: 배치 번호(머리 칸이 이미 말한다),
            순서 안내(왼쪽 목록이 곧 그 순서다), 되돌릴 수 없다는 경고(이 바 자체가 그 확인이다).
            **「AI 복사」가 아니라 「AI 변형」이다** — 시험지 목록에 실제로 붙는 배지가 그 말이고
            (PaperTable), 여기만 다른 말을 쓰고 있었다. */}
        {!assembled && !discarded && confirmAssemble && (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="flex items-start gap-1.5 text-xs leading-relaxed text-amber-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                승인한 <b>{counts.approved}문항</b>이 들어가고, 폐기한 <b>{counts.rejected}문항</b>은 빠집니다.
                새 시험지는 <b>AI 변형</b>으로 표시됩니다.
              </span>
            </p>
            {counts.approved === 0 && (
              <p className="mt-1.5 text-xs font-medium text-danger">
                승인한 문항이 하나도 없습니다 — 넣을 것이 없어 서버가 거절합니다.
              </p>
            )}
            {cardDirty && (
              <p className="mt-1.5 rounded-lg bg-amber-100/70 px-2 py-1.5 text-xs leading-relaxed text-amber-900">
                <b>저장하지 않은 검수 수정이 있습니다</b> — 지금 만들면 담기지 않고, 새 시험지로 넘어가면 사라집니다.
                {' '}지금 고친 내용까지 넣으려면 검수 카드에서 먼저 저장하세요.
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                onClick={assemble}
                loading={assembling}
                disabled={assembling || discarding}
                icon={<Package className="size-3.5" />}
              >
                생성 확정
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmAssemble(false)} disabled={assembling}>
                취소
              </Button>
            </div>
          </div>
        )}
        {/* 안내 줄은 왼쪽 칸, 버튼은 그 오른쪽 — 상자의 오른쪽 아래 구석에 붙는다(오너 지시 2026-10-09
            「좌측 2행의 문장 우측에, 부모 요소의 우측 하단에」). 줄 사이 여백은 각 줄이 그대로 갖는다(묶음은 여백을 더하지 않는다). */}
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            {assembled && (
              <p className="mt-1.5 text-xs font-medium text-gray-700">
                이 배치로 이미 시험지를 만들었습니다{resultPaperLabel ? ` — 새 시험지 ${resultPaperLabel}` : ''}.
                다시 만들면 같은 시험지가 한 벌 더 생깁니다.
              </p>
            )}
            {discarded ? (
              <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
                이 AI변형 시험지는 <b>폐기</b>했습니다 — 새 시험지를 만들 수 없습니다.
                {counts.approved > 0 && ` 승인한 ${counts.approved}문항은 문제은행에 그대로 있습니다.`}
                {' '}다시 만들려면 원본 시험지에서 AI 복사를 다시 거세요.
              </p>
            ) : (
              <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
                모든 문항이 <b>승인</b> 또는 <b>폐기</b>로 정리돼야 새 시험지를 만들 수 있습니다.
                일부만 넣으려면 나머지를 <b>폐기</b>하면 됩니다.
              </p>
            )}
            {/* 다 정리됐다는 말은 적지 않는다(오너 지시 2026-08-31) — 그 사실은 옆 [시험지 생성]
                버튼이 켜지는 것으로 말한다. 아직 남았을 때만 버튼이 꺼져 있는 이유를 숫자로 말한다.
                unsettled 는 늘 멤버에서 세므로(tally) 아래 두 줄은 동시에 뜨지 않는다. */}
            {members.length === 0 && (
              <p className="mt-1 text-xs font-medium text-gray-700">문항을 불러오는 중입니다.</p>
            )}
            {counts.unsettled > 0 && (
              <p className="mt-1 text-xs font-medium text-gray-700">
                아직 {counts.unsettled}문항이 미검수입니다 — 정리하면 [시험지 생성]이 열립니다.
              </p>
            )}
            {/* 409 설명은 걷었다(오너 지시 2026-08-31) — 아직 일어나지도 않은 서버 응답 코드를
                늘 띄워 두던 줄이다. 미검수가 남았다는 사실은 바로 위 줄이 숫자로 말하고, 실제로
                거절당하면 그때 error 로 뜬다. 남기는 것은 **진행 중**이라는 사실뿐이다. */}
            {assembling && (
              <p className="mt-1 text-[10px] text-gray-400">
                새 시험지를 만드는 중입니다 — 다 만들어질 때까지 기다려 주세요.
              </p>
            )}
          </div>
          {/* 상자의 일 — 머리 줄 끝에서 이 구석으로 옮겼다(오너 지시 2026-10-09). 같은 자리의 두 얼굴이라
              [만든 시험지 보기]도 함께 옮긴다 — 상태에 따라 버튼이 위아래로 뛰지 않게. */}
          {assembled ? (
            assembledPaperId !== '' && onAssembled ? (
              <Button size="sm" variant="secondary" onClick={() => onAssembled(assembledPaperId)} className="shrink-0">
                만든 시험지 보기
              </Button>
            ) : null
          ) : !confirmAssemble && !discarded && (
            <Button
              size="sm"
              onClick={() => setConfirmAssemble(true)}
              disabled={!allSettled || assembling || discarding}
              icon={<Package className="size-3.5" />}
              className="shrink-0"
            >
              시험지 생성
            </Button>
          )}
        </div>
      </div>

      {loading && members.length === 0 && (
        <p className="shrink-0 rounded-xl border border-gray-100 p-4 text-center text-sm text-gray-400">배치를 불러오는 중…</p>
      )}

      {error && (
        <div role="alert" className="shrink-0 space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
          <Button size="xs" variant="secondary" onClick={() => load()}>다시 시도</Button>
        </div>
      )}

      {!error && !loading && members.length === 0 && (
        <p className="shrink-0 rounded-xl border border-gray-100 p-4 text-center text-sm text-gray-400">
          이 배치에 문항이 없습니다 — 원본 시험지에 문항이 없었거나 배치가 잘못 만들어졌습니다.
        </p>
      )}

      {members.length > 0 && (
        <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
          <div className="flex shrink-0 flex-col gap-2 lg:min-h-0 lg:w-80">
            {/* 문항 목록 — 스스로 스크롤한다. 좁은 화면(위아래로 쌓일 때)은 화면의 1/3 까지만
                차지한다. 그 아래 검수 카드가 첫 화면에 함께 보여야 무엇을 고른 것인지 읽힌다.
                넓은 화면에서는 옆으로 서므로 그 상한을 푼다(칸 높이만큼 늘어난다). */}
            <ul className="max-h-[35vh] space-y-1.5 overflow-y-auto pr-1 lg:max-h-none lg:min-h-0 lg:flex-1">
              {members.map((m, idx) => {
                const vid = memberVid(m);
                const order = m?.order_no ?? idx + 1;
                const src = sourceProblemId(m);
                const st = getJobStatusDisplay(m?.status);
                const rv = getReviewStatusDisplay(m?.review_status);
                const selected = vid != null && String(vid) === String(selectedVid);
                // 줄 전체가 누르는 자리다 — 위 줄만이 아니라 아래 안내 줄·실패 줄을 눌러도 이 문항이 열린다
                // (오너 지시 2026-10-09). 누르기는 <li> 한 곳에서만 받는다: 위 줄 버튼에도 걸면 한 번 누름이
                // 두 번 돌아 미저장 확인창이 두 번 뜬다. 버튼은 키보드 자리로 남는다(Enter 의 click 이 여기로 올라온다).
                // 버튼을 줄 전체로 늘이는 덮개(::after absolute)는 이 저장소에서 안 먹는다 — 전역 preserve-3d 탓에
                // absolute 가 바로 위 부모(버튼) 기준이 된다. 올림 표시는 문제 v2 검색 목록 카드와 같다(오너 결정 같은 날).
                return (
                  <li
                    key={vid ?? `idx-${idx}`}
                    onClick={vid != null ? () => selectMember(m) : undefined}
                    className={`rounded-xl border transition-colors ${
                      selected
                        ? 'cursor-pointer border-primary/40 bg-primary/5'
                        : vid != null
                          ? 'cursor-pointer border-gray-200 hover:border-primary/30 hover:bg-gray-50'
                          : 'border-gray-200'
                    }`}
                  >
                    <button
                      type="button"
                      disabled={vid == null}
                      aria-current={selected}
                      className="flex w-full items-center gap-1.5 p-2 text-left disabled:opacity-50"
                    >
                      <span className="w-6 shrink-0 text-[11px] font-medium text-gray-400">{order}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                      {rv && m?.review_status !== 100 && (
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${rv.cls}`}>{rv.text}</span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-[11px] text-gray-500">
                        {/* 이 줄 전체가 button 안이다 — 번호에 링크를 걸면 버튼 안의 버튼이 된다.
                            얼굴(고정폭·굵기)만 정본을 따르고 색은 이 줄이 갖는다. */}
                        {src != null ? <>원본 <ProblemIdLink id={src} chip linked={false} /></> : '원본 미상'}
                        {m?.description ? ` · ${m.description}` : ''}
                      </span>
                    </button>
                    {m?.status === 500 && (
                      <div role="alert" className="space-y-1.5 border-t border-red-100 bg-red-50 p-2">
                        <p className="text-[11px] text-red-700">생성 실패{m.error ? ` — ${m.error}` : ''}</p>
                        {/* 폐기한 문항은 재시도가 409 다(회신 §2) — 눌러도 안 되는 버튼을 세우지 않는다 */}
                        {m?.review_status !== 400 && (
                          <Button
                            size="xs"
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); retryMember(m); }}
                            loading={retryingVid != null && String(retryingVid) === String(vid)}
                            disabled={vid == null || retryingVid != null || discarding}
                          >
                            다시 시도
                          </Button>
                        )}
                      </div>
                    )}
                    {/* 정리되지 않은 문항은 어느 상태에서든 여기서 폐기할 수 있다 — 검수 카드의 폐기는
                        생성이 끝난 뒤에만 열려, 대기·생성 중에 멈춘 한 건이 배치 전체를 잠근다 */}
                    {vid != null && !isSettled(m) && (
                      <div className="flex items-center gap-1.5 border-t border-gray-100 px-2 py-1">
                        <span className="min-w-0 flex-1 truncate text-[10px] text-gray-400">
                          {m?.status === 500
                            ? '되살릴 수 없으면 폐기해야 조립이 열립니다.'
                            : '폐기하면 정리된 것으로 세어 조립이 열립니다.'}
                        </span>
                        {/* 휴지통 아이콘 — 모양은 같은 메뉴의 [테스트 삭제](AssignmentPanel)와 같고, 색은 기본부터 빨강 계열이다
                          (오너 지시 2026-10-09 · 폴더 [삭제]의 휴지통과 같은 red-400, 올리면 종전 [폐기]의 red-50/600).
                            글자가 없어 이름은 aria-label·title 이 말한다. 폐기 중인 줄은 아이콘 자리에 도는 표시를 둔다
                            (종전 「폐기 중…」 글자가 하던 일). */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); rejectMember(m); }}
                          disabled={rejectingVid != null || assembling || discarding || approvingAll != null}
                          aria-label={`${order}번 문항 폐기`}
                          title="폐기"
                          className="shrink-0 rounded-full p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        >
                          {String(rejectingVid) === String(vid)
                            ? <span aria-hidden="true" className="block size-3.5 animate-spin rounded-full border-b-2 border-current" />
                            : <Trash2 aria-hidden="true" className="size-3.5" />}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {/* 전체 승인 — 목록 칸의 **바닥에 붙박는다**(오너 지시 2026-10-09 「카드 요소 아래가 아니라 위 요소
                하단에 고정」). 스크롤 안 끝 줄 다음에 두면 문항이 많을 때 끝까지 내려야 보인다. 목록에 하는 일이라
                목록 곁에 선다 — 「새 시험지 만들기」 상자에 두면 검수와 생성 두 단계 버튼이 한 상자에 섞였다.
                색은 메인 녹색(오너 지시 같은 날), 아이콘은 검수 카드의 [승인 — 문제로 등록]과 같은 체크다.
                왼쪽에 맞춘다(오너 지시 같은 날). 다 정리되면 꺼진 채 남아 「더 승인할 것이 없다」를 말하고, 시험지를 만든 배치에서는 감춘다. */}
            {!assembled && !discarded && (
              <div className="flex shrink-0">
                <Button
                  size="sm"
                  onClick={approveAll}
                  loading={approvingAll != null}
                  disabled={approvable.length === 0 || assembling || discarding}
                  icon={<Check className="size-3.5" />}
                >
                  {approvingAll ? `승인 중 ${approvingAll.cur}/${approvingAll.total}` : '전체 승인'}
                </Button>
              </div>
            )}
          </div>

          {/* 검수 — 이전/다음 내비(계약 §5-3)를 카드 위아래에 둔다 */}
          <div ref={reviewRef} className="flex min-h-64 min-w-0 flex-1 flex-col rounded-xl border border-gray-200">
            {/* 이전/다음은 늘 같은 자리에 서 있어야 한다 — 카드와 함께 흘러가면 긴 문항에서
                다음으로 넘어가려고 카드 끝까지 내려야 한다 */}
            <div className="flex shrink-0 flex-wrap items-center gap-1 border-b p-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={!prevMember || assembling}
                className="flex items-center gap-0.5 rounded-full px-2.5 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="size-4" />
                이전
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={!nextMember || assembling}
                className="flex items-center gap-0.5 rounded-full px-2.5 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                다음
                <ChevronRight className="size-4" />
              </button>
              <span className="text-[11px] text-gray-400">
                {curIdx >= 0 ? `${curIdx + 1} / ${members.length}문항` : `${members.length}문항`}
                <span className="ml-1 hidden sm:inline">· ← → 키로도 이동</span>
              </span>
              <span className="ml-auto" />
              {/* 조립을 여는 유일한 조건이 '미검수 0'이라, 남은 것으로 바로 뛰는 길을 붙인다 */}
              <button
                type="button"
                onClick={goNextUnsettled}
                disabled={counts.unsettled === 0 || assembling}
                className="flex items-center gap-0.5 rounded-full px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-30"
              >
                <SkipForward className="size-3.5" />
                다음 미검수로
              </button>
            </div>

            <div ref={cardScrollRef} className="min-h-0 flex-1 overflow-y-auto p-3">
              {selectedVid == null ? (
                <p className="p-4 text-center text-sm text-gray-400">
                  {discarded
                    ? '왼쪽에서 문항을 고르면 내용을 볼 수 있습니다.'
                    : '왼쪽에서 문항을 고르면 검수 카드가 열립니다 — 승인하거나 폐기하면 조립에 한 걸음 가까워집니다.'}
                </p>
              ) : (
                // key 로 문항마다 새로 마운트한다 — 카드의 편집 state 가 앞 문항 값으로 잠깐 비치는 것을 막는다.
                // cardNonce 는 [전체 승인]이 올린다 — 카드 밖에서 승인한 결과를 카드가 다시 읽게
                <VariationReviewCard
                  key={`${selectedVid}-${cardNonce}`}
                  kind="variation"
                  id={selectedVid}
                  /* 원본 대조를 은행 본문으로 그린다 — 멤버가 원본 번호를 갖고 있다 */
                  sourceProblemId={sourceProblemId(members.find((m) => String(memberVid(m)) === String(selectedVid)))}
                  onChanged={() => load({ silent: true })}
                  onDirtyChange={(d) => { dirtyRef.current = d; setCardDirty(d); onDirtyChange?.(d); }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BatchPanel;
